import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Images from "../utils/Images";

const EASE = [0.22, 1, 0.36, 1];

const PRODUCTS = [
  {
    id: "simmarket",
    index: "01",
    name: "SimMarkets",
    role: "Marketplace & acquisition",
    logo: Images.simmarket_colored,
    tagline: "Learn. Compare. Decide.",
    description:
      "The intelligent financial marketplace where consumers discover, compare and access trusted financial products while financial institutions receive qualified customer leads.",
    features: [
      "Financial Education",
      "Product Comparison",
      "AI Recommendations",
      "Appointment Booking",
      "Lead Generation",
      "Contact Centre as a Service",
      "Rewards & Loyalty",
    ],
    cta: "Learn more",
    href: "/simmarkets",
    visual: "market",
    art: Images.solutionMarket,
    artLabel: "01  ·  SimMarkets",
    tone: "light",
  },
  {
    id: "simverify",
    index: "02",
    name: "SimVerify",
    role: "Digital trust & verification",
    logo: Images.simverify_logo,
    tagline: "Verify before you trust",
    description:
      "An AI-powered Digital Trust Intelligence Platform that helps financial institutions verify borrowers, vehicles, devices and behavioural patterns before making lending decisions.",
    features: [
      "Vehicle Verification",
      "Device Verification",
      "SIM Intelligence",
      "AI Trust Score",
      "Fraud Detection",
      "Asset Verification",
      "APIs",
    ],
    cta: "Explore SimVerify",
    href: "/simverify",
    visual: "verify",
    art: Images.solutionVerify,
    artLabel: "02  ·  SimVerify",
    tone: "dark",
    reverse: true,
  },
  {
    id: "simcollect",
    index: "03",
    name: "SimCollect",
    role: "Intelligent collections",
    logo: Images.simcollect_logo,
    tagline: "Recover smarter",
    description:
      "A digital debt recovery platform that automates collections, customer engagement and repayment journeys while improving recovery performance and customer relationships.",
    features: [
      "Debt Portfolio Management",
      "Customer Engagement",
      "Contact Centre",
      "Automated Workflows",
      "Mobile Money",
      "USSD Payments",
      "Reporting",
      "Analytics",
    ],
    cta: "Discover SimCollect",
    href: "/simcollect",
    visual: "collect",
    art: Images.solutionCollect,
    artLabel: "03  ·  SimCollect",
    tone: "light",
  },
];

function Solutions() {
  const reduce = useReducedMotion();
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  return (
    <section
      id="solutions"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="solutions-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(229,184,11,0.1),transparent_55%),radial-gradient(55%_40%_at_100%_8%,rgba(12,31,27,0.07),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.32]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <motion.header
          className="max-w-3xl"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            One ecosystem. Three intelligent solutions.
          </p>
          <h2
            id="solutions-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
          >
            Everything financial services
            <span className="mt-1 block">needs in one platform</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-navy/55 sm:text-lg">
            A connected suite for how people find products, prove trust, and recover value.
          </p>
        </motion.header>

        <motion.nav
          className="mt-12 grid gap-px overflow-hidden border border-navy/10 bg-navy/10 sm:grid-cols-3"
          aria-label="Solutions"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          {PRODUCTS.map((product) => (
            <a
              key={product.id}
              href={`#${product.id}`}
              className="group relative bg-white px-6 py-6 transition hover:bg-[#f3f7f5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold"
            >
              <span className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-gold transition duration-300 group-hover:scale-x-100" />
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] font-semibold tracking-[0.22em] text-gold">
                  {product.index}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/30">
                  {product.name}
                </span>
              </div>
              <p className="mt-6 text-lg font-semibold tracking-tight text-navy">{product.role}</p>
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-navy/45 transition group-hover:text-navy">
                Open {product.name}
                <ArrowIcon />
              </p>
            </a>
          ))}
        </motion.nav>

        <div className="relative mt-16 space-y-8 lg:mt-20 lg:space-y-10">
          <span
            className="pointer-events-none absolute left-[11px] top-8 hidden h-[calc(100%-4rem)] w-px bg-gradient-to-b from-gold via-navy/15 to-gold lg:block"
            aria-hidden="true"
          />
          {PRODUCTS.map((product) => (
            <ProductChapter key={product.id} product={product} reduce={reduce} fade={fade} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductChapter({ product, reduce, fade }) {
  const dark = product.tone === "dark";
  const reverse = Boolean(product.reverse);

  return (
    <motion.article
      id={product.id}
      className="relative scroll-mt-28 lg:pl-12"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={fade}
    >
      <span
        className={`absolute left-0 top-10 hidden h-6 w-6 items-center justify-center rounded-full border bg-white font-mono text-[9px] font-semibold lg:flex ${
          dark ? "border-gold text-navy" : "border-navy/15 text-navy/50"
        }`}
        aria-hidden="true"
      >
        {product.index}
      </span>

      <div
        className={`relative grid border lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] ${
          dark
            ? "border-white/10 bg-navy text-white"
            : "border-navy/10 bg-white text-navy shadow-[0_24px_60px_-36px_rgba(12,31,27,0.28)]"
        } ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}
      >
        <div className="relative flex flex-col justify-center px-6 py-10 sm:px-10 sm:py-12 lg:px-12 lg:py-14">
          {dark ? (
            <>
              <div className="pointer-events-none absolute -right-16 top-0 h-48 w-48 rounded-full bg-royal/30 blur-3xl" aria-hidden="true" />
              <div className="pointer-events-none absolute -bottom-16 left-8 h-40 w-40 rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
            </>
          ) : null}

          <div className="relative flex items-center gap-3">
            <span className={`font-mono text-xs font-semibold tracking-[0.22em] ${dark ? "text-gold" : "text-navy/35"}`}>
              {product.index} / 03
            </span>
            <span className={`h-px w-8 ${dark ? "bg-gold/50" : "bg-navy/15"}`} />
            <span className={`text-[11px] font-semibold uppercase tracking-[0.2em] ${dark ? "text-white/40" : "text-navy/35"}`}>
              {product.role}
            </span>
          </div>

          <img
            src={product.logo}
            alt=""
            className="relative mt-6 h-10 w-auto max-w-[14rem] object-contain object-left sm:h-11"
          />
          <h3
            className={`relative mt-5 max-w-lg text-3xl font-semibold tracking-tight sm:text-[2.05rem] ${
              dark ? "" : "text-royal"
            }`}
          >
            {product.tagline}
          </h3>
          <p className={`relative mt-4 max-w-lg text-[15px] leading-[1.75] ${dark ? "text-white/65" : "text-navy/58"}`}>
            {product.description}
          </p>

          <ul className="relative mt-8 flex flex-wrap gap-2">
            {product.features.map((feature) => (
              <li
                key={feature}
                className={`border px-3 py-1.5 text-[12px] font-medium ${
                  dark ? "border-white/15 text-white/80" : "border-navy/10 bg-[#f3f7f5] text-navy/75"
                }`}
              >
                {feature}
              </li>
            ))}
          </ul>

          {product.href ? (
            <a
              href={product.href}
              className={`relative mt-9 inline-flex min-h-11 w-fit cursor-pointer items-center gap-2 rounded-full px-5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                dark
                  ? "bg-gold text-navy hover:bg-[#f0c52a] focus-visible:ring-gold focus-visible:ring-offset-navy"
                  : "bg-navy text-white hover:bg-royal focus-visible:ring-navy"
              }`}
            >
              {product.cta}
              <ArrowIcon />
            </a>
          ) : (
            <span
              className={`relative mt-9 inline-flex min-h-11 w-fit items-center gap-2 rounded-full px-5 text-sm font-semibold ${
                dark ? "bg-gold text-navy" : "bg-navy text-white"
              }`}
            >
              {product.cta}
            </span>
          )}
        </div>

        <ProductStage
          type={product.visual}
          art={product.art}
          artLabel={product.artLabel}
          name={product.name}
          dark={dark}
          reduce={reduce}
        />
      </div>
    </motion.article>
  );
}

function ProductStage({ type, art, artLabel, name, dark, reduce }) {
  const [artFailed, setArtFailed] = useState(false);
  const showArt = Boolean(art) && !artFailed;

  return (
    <div
      className={`relative flex h-full min-h-[18rem] items-center justify-center px-6 py-8 sm:min-h-[22rem] sm:px-8 lg:px-10 lg:py-12 ${
        dark ? "bg-white/[0.04]" : "bg-[#f3f7f5]"
      }`}
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className={`absolute right-6 top-8 h-28 w-28 rounded-full blur-3xl ${dark ? "bg-gold/20" : "bg-gold/25"}`} />
        <div className={`absolute bottom-8 left-8 h-24 w-24 rounded-full blur-3xl ${dark ? "bg-royal/35" : "bg-royal/12"}`} />
      </div>

      <span className={`pointer-events-none absolute left-4 top-4 h-7 w-7 border-l border-t ${dark ? "border-gold/70" : "border-gold"}`} />
      <span className={`pointer-events-none absolute right-4 top-4 h-7 w-7 border-r border-t ${dark ? "border-white/20" : "border-navy/15"}`} />
      <span className={`pointer-events-none absolute bottom-4 left-4 h-7 w-7 border-b border-l ${dark ? "border-white/20" : "border-navy/15"}`} />
      <span className={`pointer-events-none absolute bottom-4 right-4 h-7 w-7 border-b border-r ${dark ? "border-gold/70" : "border-gold"}`} />

      <p
        aria-hidden="true"
        className={`pointer-events-none absolute right-6 top-6 select-none font-semibold leading-none tracking-tight ${
          dark ? "text-white/[0.06]" : "text-navy/[0.05]"
        } [font-size:clamp(3rem,8vw,5.5rem)]`}
      >
        {name.replace("Sim", "")}
      </p>

      {showArt ? (
        <img
          src={art}
          alt={`${name} product illustration`}
          className="relative z-[1] h-auto max-h-[20rem] w-full object-contain lg:max-h-[24rem]"
          onError={() => setArtFailed(true)}
        />
      ) : (
        <div className="relative z-[1] w-full max-w-md">
          <p className={`mb-6 text-[11px] font-semibold uppercase tracking-[0.24em] ${dark ? "text-gold/80" : "text-navy/35"}`}>
            {artLabel} · illustration slot
          </p>
          {type === "verify" ? (
            <VerifyStage reduce={reduce} />
          ) : type === "collect" ? (
            <CollectStage reduce={reduce} />
          ) : (
            <MarketStage reduce={reduce} />
          )}
        </div>
      )}
    </div>
  );
}

function MarketStage({ reduce }) {
  const people = ["Ama", "Kojo", "Zara"];
  const products = ["Loans", "Savings", "Cover"];

  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-navy/40">
        Marketplace matching
      </p>
      <div className="mt-8 grid grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-8">
        <ul className="space-y-3">
          {people.map((name, i) => (
            <motion.li
              key={name}
              className="flex items-center gap-3 border border-navy/10 bg-white px-3 py-2.5"
              initial={reduce ? false : { opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: reduce ? 0 : i * 0.08, ease: EASE }}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-navy text-[10px] font-semibold text-white">
                {name.slice(0, 1)}
              </span>
              <span className="text-sm font-medium text-navy">{name}</span>
            </motion.li>
          ))}
        </ul>

        <div className="flex flex-col items-center gap-2">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold text-[11px] font-bold tracking-[0.12em] text-navy">
            AI
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-navy/40">
            Match
          </span>
        </div>

        <ul className="space-y-3">
          {products.map((name, i) => (
            <motion.li
              key={name}
              className="border border-navy/10 bg-white px-3 py-2.5 text-right text-sm font-medium text-navy"
              initial={reduce ? false : { opacity: 0, x: 12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: reduce ? 0 : 0.1 + i * 0.08, ease: EASE }}
            >
              {name}
            </motion.li>
          ))}
        </ul>
      </div>
      <p className="mt-8 text-sm leading-relaxed text-navy/50">
        Consumers discover the right product. Institutions receive qualified leads.
      </p>
    </div>
  );
}

function VerifyStage({ reduce }) {
  const nodes = [
    { label: "Vehicle", x: "8%", y: "18%" },
    { label: "Device", x: "72%", y: "12%" },
    { label: "SIM", x: "6%", y: "68%" },
    { label: "Asset", x: "70%", y: "72%" },
  ];

  return (
    <div className="relative mx-auto aspect-square max-w-md">
      <div className="absolute inset-[12%] rounded-full border border-dashed border-white/15" />
      <div className="absolute inset-[28%] rounded-full border border-gold/30" />
      <div className="absolute left-1/2 top-1/2 flex h-[34%] w-[34%] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-gold text-navy shadow-[0_0_40px_rgba(229,184,11,0.25)]">
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em]">Trust</span>
        <span className="mt-1 text-2xl font-semibold leading-none">92</span>
      </div>
      {nodes.map((node, i) => (
        <motion.span
          key={node.label}
          className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[11px] font-semibold text-white backdrop-blur-sm"
          style={{ left: node.x, top: node.y }}
          initial={reduce ? false : { opacity: 0, scale: 0.85 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: reduce ? 0 : 0.1 * i, ease: EASE }}
        >
          {node.label}
        </motion.span>
      ))}
    </div>
  );
}

function CollectStage({ reduce }) {
  const rows = [
    { label: "Portfolio", value: 42 },
    { label: "Engagement", value: 64 },
    { label: "Workflows", value: 78 },
    { label: "Recovered", value: 91 },
  ];

  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-navy/40">
        Recovery performance
      </p>
      <ul className="mt-8 space-y-5">
        {rows.map((row, i) => (
          <li key={row.label}>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-medium text-navy">{row.label}</span>
              <span className="font-mono text-xs text-navy/40">{row.value}%</span>
            </div>
            <div className="h-1.5 overflow-hidden bg-navy/10">
              <motion.span
                className={`block h-full ${i === rows.length - 1 ? "bg-gold" : "bg-royal"}`}
                initial={reduce ? false : { width: 0 }}
                whileInView={{ width: `${row.value}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: reduce ? 0 : 0.1 * i, ease: EASE }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path
        d="M3 8H13M13 8L9 4M13 8L9 12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default Solutions;
